import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { getUserToken } from '@/redux/user';
import {
    useGetBrandPromotionalBannersQuery,
    useDeleteBrandPromotionalBannerMutation,
    useGetBrandPromotionalBannerSettingsQuery,
    useUpdateBrandPromotionalBannerSettingsMutation,
    useGetBrandPickupSchedulesQuery,
    useGetBrandPromotionalBannerTargetsQuery,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { BrandPromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import type { TableRow } from '@/components/atoms/Table/Table.type';

import { convertTableSortToQuerySort } from '@/pages/Restaurant/PromotionalBanners/utils/convertTableSortToQuerySort';
import { useBrandBannerAction } from './hooks/useBrandBannerAction';
import { useBrandBannerToggle } from './hooks/useBrandBannerToggle';
import { buildBrandPromotionalBannerRows } from './utils/buildBrandPromotionalBannerRows';
import { getBrandPromotionalBannerHeaders } from './utils/getBrandPromotionalBannerHeaders';

const DEFAULT_SORT = [{ id: 'status', desc: false }];

export const useBrandPromotionalBannersVM = () => {
    const intl = useIntl();
    const token = useSelector(getUserToken) as string;
    const match = useRouteMatch<{ brandId: string }>();
    const brandId = Number(match.params.brandId);

    const sortQuery = useMemo(() => convertTableSortToQuerySort(DEFAULT_SORT), []);

    const { data: banners = [], isLoading: isLoadingBanners } = useGetBrandPromotionalBannersQuery(
        { token, brandId, sort: sortQuery },
        { skip: !brandId },
    );

    const { data: settings } = useGetBrandPromotionalBannerSettingsQuery({ token, brandId }, { skip: !brandId });

    const { data: brandRestaurants = [] } = useGetBrandPromotionalBannerTargetsQuery(
        { token, brandId },
        { skip: !brandId },
    );

    const { data: schedulesMap = {} } = useGetBrandPickupSchedulesQuery({ token, brandId }, { skip: !brandId });

    const [deleteBanner] = useDeleteBrandPromotionalBannerMutation();
    const [updateSettings, { isLoading: isSavingCarouselDelay }] = useUpdateBrandPromotionalBannerSettingsMutation();

    const carouselDelayMs = settings?.carouselDelayMs ?? 4000;

    const { action, clearAction, openCreateModal, openEditModal, openDeleteModal } = useBrandBannerAction();
    const { handleToggle, bulkReloadState, setBulkReloadState } = useBrandBannerToggle({
        token,
        brandId,
        schedulesMap,
    });

    const handleDelete = useCallback(
        async (banner: BrandPromotionalBanner) => {
            await deleteBanner({
                token,
                brandId,
                promotionalBannerId: banner.promotionalBannerId,
            });
            clearAction();
        },
        [token, brandId, deleteBanner, clearAction],
    );

    const saveCarouselDelay = useCallback(
        async (ms: number) => {
            await updateSettings({ token, brandId, carouselDelayMs: ms });
        },
        [token, brandId, updateSettings],
    );

    const headers = useMemo(() => getBrandPromotionalBannerHeaders(intl), [intl]);

    const rows = useMemo(
        () =>
            buildBrandPromotionalBannerRows({
                banners,
                intl,
                onToggle: handleToggle,
                onEdit: openEditModal,
                onDelete: openDeleteModal,
            }),
        [banners, intl, handleToggle, openEditModal, openDeleteModal],
    );

    const customRowProps = useCallback(
        (row: TableRow) => {
            const bannerId = row.id?.value;
            const banner = banners.find((b) => b.promotionalBannerId === bannerId);
            if (banner?.isActive) {
                return { style: { backgroundColor: '#F2FDF5' } };
            }
            return {};
        },
        [banners],
    );

    const handleRowClick = useCallback(
        (cell?: { row: { original: TableRow } }) => {
            if (!cell) return;
            const bannerId = cell.row.original.id?.value;
            const banner = banners.find((b) => b.promotionalBannerId === bannerId);
            if (banner) {
                openEditModal(banner);
            }
        },
        [banners, openEditModal],
    );

    const isLoading = isLoadingBanners;

    return {
        banners,
        headers,
        rows,
        isLoading,
        brandId,
        token,
        carouselDelayMs,
        isSavingCarouselDelay,
        brandRestaurants,
        schedulesMap,
        action,
        clearAction,
        openCreateModal,
        customRowProps,
        handleRowClick,
        handleToggle,
        handleDelete,
        saveCarouselDelay,
        bulkReload: bulkReloadState,
        setBulkReloadState,
    };
};
